import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Linking, View } from 'react-native';
import {
  Components,
  LoadingOverlay,
  Theme,
} from '@oneamerica/dxp-ui-components';
import responsiveStyle, { makeAPaymentButtonColors } from '../../style';
import InfoWrapper from '../../../../components/InfoWrapper/InfoWrapper';
import { getPolicyTitleValue } from '../../helpers';
import { analyticsTags } from '../../../../utils/Analytics/AnalyticsTags';
import { trackCustomEvent } from '../../../../utils/Analytics/AdobeAnalyticsUtils';
import { useDispatch, useSelector } from 'react-redux';
import {
  formatCurrency,
  getScreenType,
  isHomeOfficeUser,
} from '../../../../utils';
import { PNC_SHO_URL } from '../../../../config/getEnvironmentVars';
import { useFocusEffect } from '@react-navigation/native';
import { AlertLevel } from '../../../../constants/UtilsConstants';
import BreakpointConstants from '../../../../constants/BreakpointConstants';
import { GET_PAYMENTS_DATA } from '../../../../redux/payments/types';
import { ProcessingTroubleModal } from '../../../../components/ProcessingTroubleModal';
import { resetPaymentsEncryptedData } from '../../../../redux/payments/paymentsSlice';
import MakePaymentModal from '../../../PolicyFinancials/components/BillingAndPremium/MakePaymentModal';
import LegalDisclaimerModal from '../../../PolicyFinancials/components/BillingAndPremium/LegalDisclaimerModal';

// Constants
const PAYMENT_CODES = {
  BASE_PREMIUM: 'P',
  PAID_UP_ADDITION: 'U',
  LOAN_PAYMENT: 'L',
  LOAN_INTEREST: 'I',
  LOAN_PAYOFF: 'O',
} as const;

const PREMIUM_PAYMENT_CODES = new Set(['P', 'U']);
const LOAN_PAYMENT_CODES = new Set(['L', 'I', 'O']);

// Helper functions
const getPolicySessionKey = (policyNumber: string) =>
  `paymentStatus_${policyNumber}`;
const getHandledKey = (policyNumber: string) =>
  `paymentStatusHandled_${policyNumber}`;
const getPaymentTypeKey = (policyNumber: string) =>
  `paymentType_${policyNumber}`;

const handlePaymentStatusFromURL = (
  policyNumber: string,
  apiAccountNumber: string,
  apiHasPending: boolean,
  getPolicySessionKey: (pn: string) => string,
  getHandledKey: (pn: string) => string,
  redirectShownRef: { current: boolean },
  setPaymentStatus: (status: string | undefined) => void,
) => {
  const key = getPolicySessionKey(policyNumber);
  const storedStatus = sessionStorage.getItem(key);

  const urlParams = new URLSearchParams(globalThis.location.search);
  const initialRedirectIsSuccess = urlParams.get('paymentStatus') === 'success';

  const handledKey = getHandledKey(policyNumber);
  const handled = sessionStorage.getItem(handledKey) === 'true';

  const isApiPendingForThisPolicy =
    Boolean(apiHasPending) && apiAccountNumber === policyNumber;

  if (initialRedirectIsSuccess && !handled && policyNumber) {
    sessionStorage.setItem(key, 'success');
    sessionStorage.setItem(handledKey, 'true');
    redirectShownRef.current = true;
    try {
      const u = new URL(globalThis.location.href);
      u.searchParams.delete('paymentStatus');
      globalThis.history.replaceState(null, '', u.toString());
    } catch {}
    setPaymentStatus('success');
    return;
  }

  if (isApiPendingForThisPolicy) {
    if (storedStatus === 'success' && redirectShownRef.current === true) {
      setPaymentStatus('success');
      return;
    }
    setPaymentStatus('pending');
    return;
  }

  if (storedStatus === 'success') setPaymentStatus('success');
  else setPaymentStatus(undefined);
};

interface PolicyFinancialCardProps {
  className: any;
  initialPolicyData: any[];
  generalPolicyData: any;
  linkStyle: any;
  navigateToPolicyFinancials: () => void;
  row: any;
  account: any;
  loansData?: {
    amount: number;
    decimal: number;
    integer: number;
    percent: number;
    text: string | null;
    type: string;
    policyLoan?: {
      asOfDate: string;
      interestMethod: string;
      interestRateCharged: number;
      interestRateCrtd: number;
      interestRatePaid: number;
      loanBalance: number;
      loanPayOff: number;
      netInterestRate: number;
      prefLoanInterestFactor: number;
      typeOfInterestRateCharged: string;
      valueAvailable: number;
    };
  };
  showBilling: boolean;
  showLoans: boolean;
}

export const PolicyFinancialCard = ({
  className,
  initialPolicyData,
  generalPolicyData,
  linkStyle,
  navigateToPolicyFinancials,
  row,
  account,
  loansData,
  showBilling,
  showLoans,
}: PolicyFinancialCardProps) => {
  const [isModalVisible, setModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentData, setPaymentData] = useState<any>([]);
  const [paymentStatus, setPaymentStatus] = useState<string | undefined>();
  const [paymentType, setPaymentType] = useState<string | undefined>();
  const [selectedPaymentCodes, setSelectedPaymentCodes] = useState<string[]>(
    [],
  );

  const dispatch = useDispatch();

  const { width } = useContext(Theme.ResponsiveContext);
  const style = responsiveStyle();
  const screenType = getScreenType(width);
  const isMobile = screenType === 'SM';
  const phnLinkStyle = {
    style: style.phnlinkStyle,
    linkStyle: style.phnlinkStyle,
    linkHoverStyle: style.phnlinkStyle,
    hoverStyle: style.phnlinkStyle,
    focusStyle: style.phnlinkStyle,
  };

  const policyData = useSelector((state: any) => state.policies);
  const paymentEncryptedData = useSelector(
    (state: any) => state.paymentEncryptedData,
  );

  // Extract policy highlights for easier access
  const policyHighlight =
    policyData?.getPoliciesListData?.data?.policySummary?.policyHighlights?.[0];
  const accountConfig = policyHighlight?.accountConfig;
  const policyGeneralData = policyHighlight?.generalPolicyData;
  const apiAccountNumber =
    policyData?.getPoliciesListData?.data?.policySummary?.account?.number;

  // Selectors
  const hasPendingPremiumPayment = accountConfig?.hasPendingPremiumPayment;
  const hasPendingLoanPayment = accountConfig?.hasPendingLoanPayment;
  const allowLoanPayment = accountConfig?.allowLoanPayment;
  const allowPremiumPayment = accountConfig?.allowPremiumPayment;
  const allowLoanInterest = accountConfig?.allowLoanInterest;

  const [isPendingBannerVisible, setIsPendingBannerVisible] = useState(true);
  const formRef = useRef<HTMLFormElement>(null);
  const redirectShownRef = useRef(false);
  const isHomeOfcUser = isHomeOfficeUser();
  const hasBothPendingPaymentFlags =
    hasPendingPremiumPayment === true && hasPendingLoanPayment === true;

  useEffect(() => {
    const policyNumber = account?.number;
    const apiHasPending = hasPendingPremiumPayment || hasPendingLoanPayment;

    handlePaymentStatusFromURL(
      policyNumber,
      apiAccountNumber,
      apiHasPending,
      getPolicySessionKey,
      getHandledKey,
      redirectShownRef,
      setPaymentStatus,
    );

    const storedPaymentType = sessionStorage.getItem(
      getPaymentTypeKey(policyNumber),
    );
    if (storedPaymentType) {
      setPaymentType(storedPaymentType);
    }
  }, [account?.number, policyData]);

  useFocusEffect(
    React.useCallback(() => {
      setIsPendingBannerVisible(true);
    }, []),
  );

  const shouldShowPendingBanner =
    (hasPendingPremiumPayment === true || hasPendingLoanPayment === true) &&
    isPendingBannerVisible &&
    paymentStatus !== 'success';

  const shouldShowSuccessBanner = paymentStatus === 'success';

  const showMakePaymentButton = useMemo(() => {
    return (
      allowPremiumPayment === true ||
      allowLoanPayment === true ||
      allowLoanInterest === true
    );
  }, [allowPremiumPayment, allowLoanPayment, allowLoanInterest]);

  useEffect(() => {
    dispatch(resetPaymentsEncryptedData());
    setPaymentData(null);
    setIsLoading(false);
  }, [dispatch]);

  const handlePaymentEncryptedDataChange = () => {
    if (paymentEncryptedData?.data || paymentEncryptedData?.error) {
      setIsLoading(false);
    }
    if (paymentEncryptedData?.data) {
      setPaymentData(paymentEncryptedData.data);
      return;
    }
    if (paymentEncryptedData?.error) {
      trackCustomEvent(analyticsTags.CapturePaymentDataApiFailureHomePage);
      console.error('Error fetching payment data:', paymentEncryptedData.error);
      setPaymentData(null);
    }
  };

  useEffect(() => {
    handlePaymentEncryptedDataChange();
  }, [paymentEncryptedData]);

  const onPressMakeAPayment = () => {
    setIsLoading(true);
    trackCustomEvent(analyticsTags.CaptureMakeAPaymentButtonHomeTags);

    setModalVisible(true);
    setIsLoading(false);
  };

  const trackPaymentTypeAnalytics = (paymentCodes: string[]) => {
    const hasPremiumPayment = paymentCodes.some((code) =>
      PREMIUM_PAYMENT_CODES.has(code),
    );
    const hasLoanPayment = paymentCodes.some((code) =>
      LOAN_PAYMENT_CODES.has(code),
    );

    if (hasPremiumPayment && hasLoanPayment) {
      trackCustomEvent(analyticsTags.CapturePortalPremiumAndLoanPayment);
    } else if (hasPremiumPayment) {
      trackCustomEvent(analyticsTags.CapturePortalPremiumPayment);
    } else if (hasLoanPayment) {
      trackCustomEvent(analyticsTags.CapturePortalLoanPayment);
    }
  };

  const handleLegalDisclaimerAgree = () => {
    if (sessionStorage.getItem('pncCallbackRole')) {
      sessionStorage.removeItem('pncCallbackRole');
    }

    trackPaymentTypeAnalytics(selectedPaymentCodes);

    if (formRef.current) {
      formRef.current.submit();
    }
  };

  const getPaymentTypes = () => {
    const payments: { title: string; amount: number }[] = [
      {
        title: 'Base Premium',
        amount: policyGeneralData?.billingAmount || 0,
      },
      {
        title: 'Paid Up Addition',
        amount: policyGeneralData?.billRiderAmt || 0,
      },
    ];

    return payments.filter((item) => item.amount > 0);
  };

  const sendAnalytics = () => {
    trackCustomEvent(analyticsTags.CaptureMakeAPaymentCloseButtonTags);
  };

  const getSelectedPaymentsCodes = (selectedPayments): string[] => {
    const paymentMap = {
      'base premium': PAYMENT_CODES.BASE_PREMIUM,
      'paid up addition': PAYMENT_CODES.PAID_UP_ADDITION,
      'loan payment': PAYMENT_CODES.LOAN_PAYMENT,
      'loan interest': PAYMENT_CODES.LOAN_INTEREST,
      'loan payoff': PAYMENT_CODES.LOAN_PAYOFF,
    };

    const codes: string[] = [];
    selectedPayments.forEach((payment) => {
      const title = payment.title?.toLowerCase();
      if (title && paymentMap[title]) {
        codes.push(paymentMap[title]);
      }
    });

    return codes;
  };

  const showBillingSection = showBilling === true;
  const showLoansSection = showLoans === true;

  const onPressViewDetails = () => {
    navigateToPolicyFinancials();
  };

  const getPendingBannerTitle = (): string => {
    if (hasBothPendingPaymentFlags) {
      return 'You have a pending loan and premium payment transaction in progress';
    }
    if (hasPendingPremiumPayment === true) {
      return 'You have a pending premium payment transaction in progress';
    }
    return 'You have a pending loan payment transaction in progress';
  };

  const getPendingBannerContent = (): string => {
    if (hasBothPendingPaymentFlags) {
      return 'You cannot make an additional premium or loan payment until the pending transaction has processed.';
    }
    if (hasPendingPremiumPayment === true) {
      return 'You cannot make an additional premium payment until it has processed.';
    }
    return 'You cannot make an additional loan payment until it has processed.';
  };

  const getSuccessBannerTitle = (): string => {
    if (paymentType === 'Both') {
      return 'Thank you for both your Premium and Loan Payments';
    }
    if (paymentType === 'Loan') {
      return 'Thank you for your Loan Payment';
    }
    return 'Thank you for your Premium Payment';
  };

  const disableMakePaymentButton = (): boolean => {
    // Condition 1: Basic disable conditions , or Both Pending flags are true and success banner is shown
    if (
      isLoading ||
      isHomeOfcUser ||
      (shouldShowSuccessBanner && hasBothPendingPaymentFlags)
    ) {
      return true;
    }

    // Condition 2: Both pending payments are true
    if (hasPendingPremiumPayment === true && hasPendingLoanPayment === true) {
      return true;
    }

    // Condition 3: Premium payment not allowed, but loan payment allowed and has pending loan payment
    if (
      !allowPremiumPayment &&
      (allowLoanPayment === true || allowLoanInterest === true) &&
      hasPendingLoanPayment === true
    ) {
      return true;
    }

    // Condition 4: Premium payment allowed with pending, but loan payments not allowed
    if (
      allowPremiumPayment === true &&
      hasPendingPremiumPayment === true &&
      !allowLoanPayment &&
      !allowLoanInterest
    ) {
      return true;
    }

    return false;
  };

  const determinePaymentType = (selectedPayments): string => {
    const hasPremium = selectedPayments.some(
      (p) =>
        p.title?.toLowerCase() === 'base premium' ||
        p.title?.toLowerCase() === 'paid up addition',
    );
    const hasLoan = selectedPayments.some((p) =>
      ['loan payment', 'loan interest', 'loan payoff'].includes(
        p.title?.toLowerCase(),
      ),
    );

    if (hasPremium && hasLoan) {
      return 'Both';
    }
    if (hasLoan) {
      return 'Loan';
    }
    return 'Premium';
  };

  const handleMakePaymentSubmit = (selectedPayments) => {
    setIsLoading(true);
    trackCustomEvent(analyticsTags.CaptureMakeAPaymentSubmitButtonTags);

    const paymentCodes = getSelectedPaymentsCodes(selectedPayments);
    setSelectedPaymentCodes(paymentCodes);

    const loanPayment = selectedPayments.find(
      (p) => p.title?.toLowerCase() === 'loan payment',
    );
    const loanInterest = selectedPayments.find(
      (p) => p.title?.toLowerCase() === 'loan interest',
    );

    const selectedPaymentType = determinePaymentType(selectedPayments);

    if (apiAccountNumber) {
      sessionStorage.setItem(
        getPaymentTypeKey(apiAccountNumber),
        selectedPaymentType,
      );
    }

    dispatch({
      type: GET_PAYMENTS_DATA,
      payload: {
        token: policyData?.token,
        policyNumber: apiAccountNumber,
        paymentTypes: paymentCodes,
        loanAmount: loanPayment?.amount,
        loanInterestAmount: loanInterest?.amount,
        isLoading: true,
      },
    });

    setModalVisible(false);
  };

  const renderBillingDataList = () => {
    if (isMobile) {
      return (
        <View className="py-2" style={style.row}>
          <View className="items-center" style={style.flex}>
            {initialPolicyData.map((item, index) => (
              <View key={`${item}_${index}`}>
                <InfoWrapper
                  title={
                    getPolicyTitleValue(generalPolicyData, item).title || ''
                  }
                  information={
                    getPolicyTitleValue(generalPolicyData, item).value || ''
                  }
                  subTitle
                  centerAlign
                />
              </View>
            ))}
          </View>
        </View>
      );
    }

    return (
      <View className="pt-2">
        <View className="flex-row flex-wrap">
          {initialPolicyData.map((item, index) => (
            <View key={`${item}_${index}`} className="w-1/3">
              <InfoWrapper
                title={getPolicyTitleValue(generalPolicyData, item).title || ''}
                information={
                  getPolicyTitleValue(generalPolicyData, item).value || ''
                }
                subTitle
              />
            </View>
          ))}
        </View>
      </View>
    );
  };
  if (!showBillingSection && !showLoansSection) return null;

  return (
    <>
      <LoadingOverlay loading={isLoading} />
      <View className={className}>
        <View style={[style.cardShadow, row > 1 && style.flex]}>
          {shouldShowPendingBanner && (
            <Components.AlertMessage
              className="mb-4"
              showCloseButton={true}
              onCloseMessage={() => {
                setIsPendingBannerVisible(false);
              }}
              alertLevel={AlertLevel.ALERT}
              iconSize={BreakpointConstants.isLG(width) ? 52.36 : 24}
              title={getPendingBannerTitle()}
            >
              <Components.Text style={style.alertBannerContentMargin}>
                {getPendingBannerContent()}
              </Components.Text>
            </Components.AlertMessage>
          )}
          {shouldShowSuccessBanner && (
            <Components.AlertMessage
              className="mb-4"
              showCloseButton={true}
              alertLevel={AlertLevel.SUCCESS}
              iconSize={BreakpointConstants.isLG(width) ? 52.36 : 24}
              title={getSuccessBannerTitle()}
            >
              <Components.Text style={style.marginTop16}>
                {`It may take up to 5 business days for the payment to reflect; call `}
                <Components.Link
                  onPress={() => {
                    Linking.openURL('tel:800-537-6442');
                  }}
                  accessibilityLabel="contact number"
                  accessibilityRole="button"
                  {...phnLinkStyle}
                >
                  800-537-6442
                </Components.Link>
                {` to cancel & modify`}
              </Components.Text>
            </Components.AlertMessage>
          )}
          <View
            style={[
              style.cardHeader,
              isMobile && { flexDirection: 'column', alignItems: 'flex-start' },
            ]}
          >
            <Components.Text.H3
              style={[style.cardHeaderTitle, !isMobile && style.flex]}
            >
              Policy Financials
            </Components.Text.H3>
            {showMakePaymentButton && (
              <>
                <View
                  style={{ alignSelf: 'flex-end', marginTop: isMobile ? 8 : 0 }}
                >
                  <Components.Button
                    className="mx-2"
                    accessibilityRole="button"
                    id="btnOutline"
                    testID="makeAPaymentBtn"
                    theme="primary"
                    {...makeAPaymentButtonColors}
                    disabled={disableMakePaymentButton()}
                    onPress={() => {
                      onPressMakeAPayment();
                    }}
                    style={style.makeaPaymentButton}
                  >
                    Make a Payment
                  </Components.Button>
                </View>
                <form
                  ref={formRef}
                  action={PNC_SHO_URL}
                  method="post"
                  style={{ display: 'none' }}
                >
                  <input
                    type="hidden"
                    name="BillTypeLabel"
                    value={paymentData?.billTypeLabel ?? ''}
                  />
                  <input
                    type="hidden"
                    name="CompanyName"
                    value={paymentData?.companyName ?? ''}
                  />
                  <input
                    type="hidden"
                    name="AmountDueLabel"
                    value={paymentData?.amountDueLabel ?? ''}
                  />
                  <input
                    type="hidden"
                    name="EmailAddress"
                    value={paymentData?.emailAddress ?? ''}
                  />
                  <input
                    type="hidden"
                    name="FDICode"
                    value={paymentData?.fdiCode ?? ''}
                  />
                  <input
                    type="hidden"
                    name="PolicyNumber"
                    value={paymentData?.policyNumber ?? ''}
                  />
                  <input
                    type="hidden"
                    name="ClientID"
                    value={paymentData?.clientID ?? ''}
                  />
                  <input
                    type="hidden"
                    name="ID"
                    value={paymentData?.ID ?? ''}
                  />
                </form>
              </>
            )}
          </View>
          <View className="mt-4" style={style.horizontalDivider} />

          {/* Billing & Premiums Section */}
          {showBillingSection && (
            <>
              <View
                style={[
                  style.cardHeader,
                  isMobile
                    ? style.billingAndPremiumTitleSM
                    : style.billingAndPremiumTitle,
                  { marginTop: 16 },
                ]}
              >
                <Components.Text.H3 style={[style.sectionHeaderTitle]}>
                  Billing & Premiums
                </Components.Text.H3>
              </View>

              <View style={style.contentWrapper}>
                {renderBillingDataList()}
              </View>
            </>
          )}

          {/* Loans Section */}
          {showLoansSection && (
            <>
              {showBillingSection && (
                <View className="my-4" style={style.horizontalDivider} />
              )}
              <View style={{ marginTop: showBillingSection ? 0 : 16 }}>
                <View>
                  <Components.Text.H3 style={[style.sectionHeaderTitle]}>
                    Loans
                  </Components.Text.H3>
                </View>
                <View style={style.contentWrapper}>
                  <View className="pt-2">
                    <View className="flex-row flex-wrap">
                      {((loansData?.policyLoan?.loanBalance ?? 0) > 0 ||
                        (loansData?.policyLoan?.valueAvailable ?? 0) > 0) && (
                        <>
                          <InfoWrapper
                            width="w-1/2 md:w-1/3"
                            title={'Available Loan Value'}
                            information={formatCurrency(
                              loansData?.policyLoan?.valueAvailable ?? 0,
                            )}
                            subTitle
                          />
                          <InfoWrapper
                            width="w-1/2 md:w-1/3"
                            title={loansData?.type || 'Loan Balance'}
                            information={formatCurrency(
                              loansData?.policyLoan?.loanBalance ?? 0,
                            )}
                            subTitle
                          />
                          <InfoWrapper
                            width="w-1/2 md:w-1/3"
                            title={'Loan Payoff'}
                            information={formatCurrency(
                              loansData?.policyLoan?.loanPayOff ?? 0,
                            )}
                            subTitle
                          />
                        </>
                      )}
                    </View>
                  </View>
                </View>
              </View>
            </>
          )}

          <View className="my-4" style={style.horizontalDivider} />
          <Components.Link
            accessibilityLabel="View Details"
            accessibilityRole="button"
            onPress={onPressViewDetails}
            {...linkStyle}
          >
            View Details
          </Components.Link>
        </View>
      </View>

      <MakePaymentModal
        onSubmit={handleMakePaymentSubmit}
        onCloseAnalytics={sendAnalytics}
        isModalVisible={isModalVisible}
        setModalVisible={setModalVisible}
        payments={getPaymentTypes()}
        allowLoanPayment={allowLoanPayment}
        allowLoanInterest={allowLoanInterest}
        loanPayoffAmount={loansData?.policyLoan?.loanPayOff || 0}
        loanBalance={loansData?.policyLoan?.loanBalance || 0}
        allowPremiumPayment={allowPremiumPayment}
        hasPendingPremiumPayment={hasPendingPremiumPayment}
        hasPendingLoanPayment={hasPendingLoanPayment}
      />
      <LegalDisclaimerModal
        isVisible={!!paymentEncryptedData?.data}
        onAgree={handleLegalDisclaimerAgree}
        isHomePage={true}
        onClose={() => {
          dispatch(resetPaymentsEncryptedData());
          setPaymentData(null);
          setIsLoading(false);
        }}
      />
      <ProcessingTroubleModal
        showModal={!!paymentEncryptedData?.error}
        onClose={() => {
          dispatch(resetPaymentsEncryptedData());
          setIsLoading(false);
          trackCustomEvent(
            analyticsTags.CaptureProcessingTroubleModalCloseHomePage,
          );
        }}
      />
    </>
  );
};
